import React, { useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AirHeader from "../../components/AirHeader";
import Card from "../../components/card";
import AirTitle from "../../components/AirTitle";
import Menu from "../../components/menu/Menu";
import Modal from "../../components/overlay/Modal";
import { Mode } from "./types/tutor.types";
import ChatContainer from "./containers/ChatContainer";
import useTutorV2View from "./hooks/presentation/useTutorV2View";
import PromptContainer from "./containers/PromptContainer";
import { useAutoScroll } from "./hooks/useAutoScroll";
import Btn from "../../components/btn";
import { Chevron } from "../../assets/svgs/icons";
import { useChatView } from "./hooks/presentation/useChatView";
import useShareView from "./hooks/presentation/useShareView";
import { useAuth } from "./hooks/useAuth";
import { MyContext } from "../../context/MyContext";
import LoginModal from "../chat/modal/LoginModal";
import { useUIStore } from "./store";

const TutorV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLogin } = useContext(MyContext);
  const { closeNav } = useUIStore();
  const {
    isMd,
    isLoggedIn,
    suggestions,
    hasAnyMessages,
    currentGroupId,
    handleCardClick,
  } = useTutorV2View();
  const { redirectToLogin } = useAuth();

  // forceClose 상태 처리
  useEffect(() => {
    const state = location.state as { forceClose?: boolean } | null;
    if (state?.forceClose) {
      setTimeout(() => {
        closeNav();
      }, 1);
    }
  }, [location.state, closeNav]);

  console.log("HASANYMESSAGES", hasAnyMessages);

  // TTS stopTts 함수 가져오기 (PromptContainer에 전달)
  const { stopTts } = useChatView({
    mode: Mode.TUTOR,
    groupId: currentGroupId,
  });

  // 모바일 비로그인 상태 확인
  const isMobileGuest = !isMd && !isLoggedIn;

  // 토글 버튼 핸들러
  const handleToggleToSocrates = () => {
    navigate("/socrates");
  };

  const {
    scrollRef,
    isFreeScroll,
    fetching,
    hasMessages,
    disableFreeScrollAndScroll,
  } = useAutoScroll();

  // Share modal
  const { shareModalOpen, handleOpenChange, shareUrl, copySuccess, onCopy } =
    useShareView();

  // PC 로그인 후: 사이드바 + 메인 영역
  // Note: App.tsx의 v2-layout에서 flex-1 min-h-0 overflow-hidden이 적용됨
  // 모바일에서는 HeaderMo(56px)가 App 레벨에서 렌더링되고, AirHeader(56px)가 추가로 렌더링됨
  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      {/* Sidebar */}
      {isLoggedIn ? <Menu mode={Mode.TUTOR} /> : null}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
        {/* Header */}
        <AirHeader type="main" />

        {/* Main Content Container - 스크롤 영역 */}
        <div
          ref={scrollRef}
          className={`flex-1 overflow-y-auto px-4 min-h-0 ${
            !isMd ? "pt-4 pb-0" : "py-8"
          }`}
        >
          <div
            className={`flex flex-col gap-4 md:gap-8 w-full h-full items-center ${
              hasAnyMessages ? "" : "md:justify-center"
            }`}
          >
            {/* 모바일: 토글 버튼 (채팅 진행 중이 아닐 때만 표시) */}
            {!isMd && !hasAnyMessages && (
              <div className="flex items-center justify-center shrink-0">
                <div className="flex items-center justify-center bg-[#f7f7f7] border border-[#eee] rounded-[40px]">
                  <button className="flex items-center justify-center px-5 py-2 bg-[#212121] rounded-[40px]">
                    <span className="text-sm font-bold text-white tracking-[-0.28px]">
                      튜터
                    </span>
                  </button>
                  <button
                    className="flex items-center justify-center px-5 py-2"
                    onClick={handleToggleToSocrates}
                  >
                    <span className="text-sm font-medium text-[#888] tracking-[-0.28px]">
                      소크라테스
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!hasAnyMessages ? (
              // 메시지가 없을 때: Suggestion Cards 표시
              <div
                className={`flex flex-col gap-4 md:gap-8 items-center w-full ${
                  !isMd ? "flex-1 justify-center" : "justify-center"
                }`}
              >
                <AirTitle
                  mode="tutor"
                  title="이런 질문을 할 수 있어요"
                  subtitle={`작은 호기심이 큰 발견으로 이어집니다.\n지금 궁금한 걸 물어보세요!`}
                />

                {/* Suggestion Cards Grid */}
                <div className="flex flex-col md:flex-row flex-wrap gap-4 items-start justify-center max-w-[800px] w-full md:flex-wrap">
                  {suggestions.map((card, index) => (
                    <Card.Suggestion
                      key={index}
                      title={card.title}
                      description={card.description}
                      onClick={() => handleCardClick(card.description)}
                      className="w-[100%] md:min-w-[312px] flex-1"
                      titleColor={card.titleColor}
                    />
                  ))}
                </div>
              </div>
            ) : (
              // 채팅 히스토리가 있을 때: 채팅 컨테이너 표시
              <ChatContainer mode={Mode.TUTOR} groupId={currentGroupId} />
            )}
          </div>
        </div>

        {/* Free Scroll Button - 사용자가 위로 스크롤했을 때 표시 */}
        {isFreeScroll && !fetching && hasMessages && (
          <div className="absolute bottom-28 right-8">
            <Btn.Fab
              color="light"
              onClick={disableFreeScrollAndScroll}
              aria-label="Scroll to bottom"
            >
              <Chevron className="w-6 h-6 rotate-90" />
            </Btn.Fab>
          </div>
        )}

        {/* Footer with Prompt Input or Login CTA */}
        {isMobileGuest ? (
          // 모바일 비로그인: 하단 고정 로그인 버튼
          <div className="flex-shrink-0 flex flex-col items-center justify-center p-4 w-full bg-white">
            <button
              onClick={redirectToLogin}
              className="w-full max-w-[800px] h-12 rounded-[24px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] hover:from-[#ab38a6] hover:via-[#4927b9] hover:to-[#2b78cf] text-white font-bold text-base tracking-[-0.32px] transition-all duration-200"
            >
              로그인 후 질문하기
            </button>
          </div>
        ) : (
          <div
            className="flex-shrink-0 flex flex-col items-center justify-end p-4 w-full bg-gradient-to-b from-transparent via-white/20 to-white pb-safe"
            style={{
              padding: !isMd && isLoggedIn ? "16px 0 0" : undefined,
              paddingBottom: !isMd
                ? "env(safe-area-inset-bottom, 16px)"
                : undefined,
            }}
          >
            <PromptContainer stopTts={stopTts} />
          </div>
        )}
      </div>

      {/* Share Modal */}
      <Modal
        type="sub"
        open={shareModalOpen}
        onOpenChange={handleOpenChange}
        shareUrl={shareUrl}
        onCopy={onCopy}
        copySuccess={copySuccess}
      />

      {/* Login Modal */}
      {isLogin && <LoginModal />}
    </div>
  );
};

export default TutorV2;
